import { array, boolean, date, object, string } from "yup";
import { REQUIRED_FIELD } from "@shared-kernel/primary/forms/constants";
import type { DateScheduleConfig } from "./training-schedule-form.types";
import type { ApiTrainingSchedule } from "@shared-kernel/core/types/api-training-schedule";
import { TEACHING_TEAM_MEMBER_TYPE } from "@academy-context/shared/domain/types/enums/teaching-team-member-type";
import type { CreateBatchTrainingScheduleInput, SpeakerRef } from "@shared-kernel/application/ports/teacher/teacher-repository";
import {
  DEFAULT_MORNING_START,
  DEFAULT_MORNING_END,
  DEFAULT_AFTERNOON_START,
  DEFAULT_AFTERNOON_END,
  HALF_DAY_SCHEDULE_ERRORS,
} from "./training-schedule-form.constants";
import { hasAtLeastOneHalfDay, hasHalfDayOverlap, timeStringToDate } from "./training-schedule-form.utils";

/**
 * Convert form DateScheduleConfig[] to API training schedules
 * Form uses Date object + time strings, API expects ISO datetime strings
 */
export const convertFormSchedulesToApiSchedules = (formSchedules: DateScheduleConfig[]): ApiTrainingSchedule[] => {
  return formSchedules
    .map(schedule => ({
      morning: {
        enabled: schedule.morning.enabled,
        start: timeStringToDate(schedule.date, schedule.morning.start).toISOString(),
        end: timeStringToDate(schedule.date, schedule.morning.end).toISOString(),
      },
      afternoon: {
        enabled: schedule.afternoon.enabled,
        start: timeStringToDate(schedule.date, schedule.afternoon.start).toISOString(),
        end: timeStringToDate(schedule.date, schedule.afternoon.end).toISOString(),
      },
    }))
    .sort((a, b) => {
      // Extract date from morning.start or afternoon.start
      const dateA = a.morning.enabled ? a.morning.start : a.afternoon.start;
      const dateB = b.morning.enabled ? b.morning.start : b.afternoon.start;
      return new Date(dateA).getTime() - new Date(dateB).getTime();
    });
};

/**
 * Extract time string (HH:mm) from a Date or ISO datetime string
 */
const extractTimeString = (datetime: Date | string): string => {
  const d = typeof datetime === "string" ? new Date(datetime) : datetime;
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

/**
 * Extract date from an API schedule (uses morning.start or afternoon.start)
 */
const extractDateFromApiSchedule = (schedule: ApiTrainingSchedule): Date => {
  // Get date from morning.start if enabled, otherwise from afternoon.start
  const datetimeString = schedule.morning.enabled ? schedule.morning.start : schedule.afternoon.start;
  const date = new Date(datetimeString);
  // Reset to start of day
  date.setHours(0, 0, 0, 0);
  return date;
};

/**
 * Convert API training schedules to form DateScheduleConfig[]
 * API stores dates as ISO datetime strings, form uses Date object + time strings
 */
export const convertApiSchedulesToFormSchedules = (apiSchedules: ApiTrainingSchedule[]): DateScheduleConfig[] => {
  return apiSchedules
    .map(schedule => {
      const date = extractDateFromApiSchedule(schedule);

      return {
        date,
        morning: {
          enabled: schedule.morning.enabled,
          start: schedule.morning.enabled ? extractTimeString(schedule.morning.start) : DEFAULT_MORNING_START,
          end: schedule.morning.enabled ? extractTimeString(schedule.morning.end) : DEFAULT_MORNING_END,
        },
        afternoon: {
          enabled: schedule.afternoon.enabled,
          start: schedule.afternoon.enabled ? extractTimeString(schedule.afternoon.start) : DEFAULT_AFTERNOON_START,
          end: schedule.afternoon.enabled ? extractTimeString(schedule.afternoon.end) : DEFAULT_AFTERNOON_END,
        },
      };
    })
    .sort((a, b) => a.date.getTime() - b.date.getTime());
};

/**
 * Yup validation schema for a single DateScheduleConfig
 */
const dateScheduleConfigSchema = object()
  .shape({
    date: date().required(REQUIRED_FIELD).typeError("Date invalide"),
    morning: object().shape({
      enabled: boolean().required(),
      start: string().required(),
      end: string().required(),
    }),
    afternoon: object().shape({
      enabled: boolean().required(),
      start: string().required(),
      end: string().required(),
    }),
  })
  .test("at-least-one-interval", HALF_DAY_SCHEDULE_ERRORS.noHalfDayEnabled, value => hasAtLeastOneHalfDay(value ?? {}))
  .test("no-overlap", HALF_DAY_SCHEDULE_ERRORS.overlap, value => !hasHalfDayOverlap(value ?? {}));

/**
 * Yup validation schema for trainingSchedules array field
 */
export const trainingSchedulesValidationSchema = array()
  .min(1, "Au moins une journée de formation est requise")
  .of(dateScheduleConfigSchema);

/**
 * Get a stable key for a teaching team member
 */
export const getTeachingTeamMemberKey = (member: SpeakerRef): string => {
  if (member.type === TEACHING_TEAM_MEMBER_TYPE.PLATFORM_TEACHER) return `teacher:${member.teacherId}`;
  return `external:${member.externalTeacherId}`;
};

interface DateScheduleWithSpeakers extends DateScheduleConfig {
  morning: DateScheduleConfig["morning"] & { speakerKeys: string[] };
  afternoon: DateScheduleConfig["afternoon"] & { speakerKeys: string[] };
}

/**
 * Convert form DateScheduleConfig[] to batch-specific training schedules with speakers (SpeakerRef[])
 */
export const convertFormSchedulesToBatchSchedules = (
  formSchedules: DateScheduleWithSpeakers[],
  teachingTeamMembers: SpeakerRef[],
): CreateBatchTrainingScheduleInput[] => {
  const keyToRef = new Map<string, SpeakerRef>();
  for (const member of teachingTeamMembers) {
    const key = getTeachingTeamMemberKey(member);
    if (member.type === TEACHING_TEAM_MEMBER_TYPE.PLATFORM_TEACHER) {
      keyToRef.set(key, { type: TEACHING_TEAM_MEMBER_TYPE.PLATFORM_TEACHER, teacherId: member.teacherId });
    } else {
      keyToRef.set(key, { type: TEACHING_TEAM_MEMBER_TYPE.EXTERNAL, externalTeacherId: member.externalTeacherId });
    }
  }

  const toSpeakerRefs = (speakerKeys: string[]): SpeakerRef[] => {
    return speakerKeys.map(key => keyToRef.get(key)).filter((ref): ref is SpeakerRef => ref !== undefined);
  };

  return formSchedules
    .map(schedule => ({
      morning: {
        enabled: schedule.morning.enabled,
        start: timeStringToDate(schedule.date, schedule.morning.start).toISOString(),
        end: timeStringToDate(schedule.date, schedule.morning.end).toISOString(),
        speakers: toSpeakerRefs(schedule.morning.speakerKeys),
      },
      afternoon: {
        enabled: schedule.afternoon.enabled,
        start: timeStringToDate(schedule.date, schedule.afternoon.start).toISOString(),
        end: timeStringToDate(schedule.date, schedule.afternoon.end).toISOString(),
        speakers: toSpeakerRefs(schedule.afternoon.speakerKeys),
      },
    }))
    .sort((a, b) => {
      const dateA = a.morning.enabled ? a.morning.start : a.afternoon.start;
      const dateB = b.morning.enabled ? b.morning.start : b.afternoon.start;
      return new Date(dateA).getTime() - new Date(dateB).getTime();
    });
};
